import React, { useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { Trash2, Ban, CheckCircle2, XCircle } from "lucide-react";

const err = (e, fb) => toast.error(e.response?.data?.detail || fb);

export const UsersPanel = ({ users, reload }) => {
  const [filter, setFilter] = useState("");
  const shown = filter ? users.filter((u) => u.role === filter) : users;
  const block = async (u) => {
    try { await api.patch(`/admin/users/${u.user_id}/block`, { blocked: !u.is_blocked }); toast.success(u.is_blocked ? "User unblocked" : "User blocked"); reload(); }
    catch (e) { err(e, "Could not update user"); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? This can't be undone.`)) return;
    try { await api.delete(`/admin/users/${u.user_id}`); toast.success("User removed"); reload(); }
    catch (e) { err(e, "Could not remove user"); }
  };
  return (
    <div className="mt-6 glass rounded-3xl overflow-hidden" data-testid="admin-users-panel">
      <div className="p-4 border-b border-cyan-500/15 flex items-center justify-between flex-wrap gap-2">
        <div className="heading-font font-bold">Users ({shown.length})</div>
        <div className="flex gap-1">
          {["", "customer", "merchant", "rider", "admin"].map((r) => (
            <button key={r || "all"} onClick={() => setFilter(r)} data-testid={`admin-user-filter-${r || "all"}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${filter === r ? "btn-neon" : "bg-white/5 text-slate-300"}`}>{r || "all"}</button>
          ))}
        </div>
      </div>
      <div className="divide-y divide-cyan-500/10 max-h-[70vh] overflow-y-auto">
        {shown.length === 0 && <div className="p-6 text-sm text-slate-400">No users yet.</div>}
        {shown.map((u) => (
          <div key={u.user_id} className="p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`admin-user-${u.user_id}`}>
            <div className="min-w-0">
              <div className="font-semibold truncate">{u.name} {u.is_blocked && <span className="ml-2 text-[10px] font-bold text-rose-300 border border-rose-500/30 rounded-full px-2">BLOCKED</span>}</div>
              <div className="text-xs text-slate-500 truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full border bg-white/5 border-cyan-500/15 text-cyan-300">{u.role}</span>
              {u.role !== "admin" && (
                <>
                  <Button size="sm" variant="outline" className="bg-transparent border-cyan-500/20 text-slate-200" onClick={() => block(u)} data-testid={`admin-block-btn-${u.user_id}`}>
                    <Ban size={13} className="mr-1" /> {u.is_blocked ? "Unblock" : "Block"}
                  </Button>
                  <Button size="sm" variant="outline" className="bg-transparent border-rose-500/30 text-rose-300" onClick={() => remove(u)} data-testid={`admin-remove-btn-${u.user_id}`}>
                    <Trash2 size={13} />
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ShopsPanel = ({ shops, reload }) => {
  const act = async (s, action) => {
    try { await api.patch(`/admin/shops/${s.shop_id}/${action}`); toast.success(action === "approve" ? "Shop approved" : "Shop hidden"); reload(); }
    catch (e) { err(e, "Could not update shop"); }
  };
  return (
    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-shops-panel">
      {shops.length === 0 && <div className="glass rounded-2xl p-6 text-sm text-slate-400">No shops registered yet.</div>}
      {shops.map((s) => (
        <div key={s.shop_id} className="glass rounded-2xl p-4" data-testid={`admin-shop-${s.shop_id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="heading-font font-bold truncate">{s.name}</div>
              <div className="text-xs text-slate-400">{s.category} · {s.eta_minutes}m</div>
            </div>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${s.is_approved ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`} data-testid={`admin-shop-status-${s.shop_id}`}>
              {s.is_approved ? "APPROVED" : "PENDING"}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-1 truncate">{s.address}</div>
          <div className="mt-3 flex gap-2">
            {!s.is_approved && <Button size="sm" className="btn-neon rounded-full" onClick={() => act(s, "approve")} data-testid={`admin-approve-shop-${s.shop_id}`}><CheckCircle2 size={13} className="mr-1" /> Approve</Button>}
            {s.is_approved && <Button size="sm" variant="outline" className="bg-transparent border-rose-500/30 text-rose-300 rounded-full" onClick={() => act(s, "reject")} data-testid={`admin-reject-shop-${s.shop_id}`}><XCircle size={13} className="mr-1" /> Hide</Button>}
          </div>
        </div>
      ))}
    </div>
  );
};

export const CategoriesPanel = ({ categories, reload }) => {
  const [form, setForm] = useState({ name: "", image_url: "" });
  const add = async () => {
    try { await api.post("/admin/categories", { name: form.name, image_url: form.image_url || null }); setForm({ name: "", image_url: "" }); toast.success("Category added"); reload(); }
    catch (e) { err(e, "Could not add category"); }
  };
  const del = async (c) => {
    try { await api.delete(`/admin/categories/${c.category_id}`); toast.success("Category removed"); reload(); }
    catch (e) { err(e, "Could not remove category"); }
  };
  return (
    <div className="mt-6 glass rounded-3xl p-5" data-testid="admin-categories-panel">
      <div className="heading-font font-bold">Categories</div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Input placeholder="Category name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-cyan-500/15 max-w-xs" data-testid="admin-category-name" />
        <Input placeholder="Image URL (optional)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-white/5 border-cyan-500/15 max-w-sm" data-testid="admin-category-image" />
        <Button className="btn-neon rounded-full" onClick={add} disabled={form.name.trim().length < 2} data-testid="admin-category-add">Add</Button>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {categories.map((c) => (
          <span key={c.category_id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-cyan-500/15 text-sm" data-testid={`admin-category-${c.category_id}`}>
            {c.name}
            <button onClick={() => del(c)} className="text-rose-300 hover:text-rose-200" data-testid={`admin-category-del-${c.category_id}`}><Trash2 size={12} /></button>
          </span>
        ))}
      </div>
    </div>
  );
};

export const SettingsPanel = ({ settings, reload }) => {
  const [f, setF] = useState(settings);
  const save = async () => {
    try { await api.patch("/admin/settings", { delivery_fee: Number(f.delivery_fee), free_delivery_min: Number(f.free_delivery_min) }); toast.success("Delivery fees saved"); reload(); }
    catch (e) { err(e, "Could not save settings"); }
  };
  return (
    <div className="mt-6 glass rounded-3xl p-5 max-w-lg" data-testid="admin-settings-panel">
      <div className="heading-font font-bold">Delivery fees</div>
      <p className="text-xs text-slate-400 mt-1">One fee for every order, free above the minimum order amount.</p>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <label className="text-xs text-slate-400">Delivery fee (₹)
          <Input type="number" min="0" value={f.delivery_fee} onChange={(e) => setF({ ...f, delivery_fee: e.target.value })} className="mt-1 bg-white/5 border-cyan-500/15" data-testid="admin-delivery-fee" />
        </label>
        <label className="text-xs text-slate-400">Free delivery above (₹)
          <Input type="number" min="0" value={f.free_delivery_min} onChange={(e) => setF({ ...f, free_delivery_min: e.target.value })} className="mt-1 bg-white/5 border-cyan-500/15" data-testid="admin-free-min" />
        </label>
      </div>
      <Button className="btn-neon rounded-full mt-4" onClick={save} data-testid="admin-settings-save">Save</Button>
    </div>
  );
};

export const AssignRider = ({ order, riders, reload }) => {
  const assign = async (rider_id) => {
    try { await api.patch(`/admin/orders/${order.order_id}/assign`, { rider_id }); toast.success("Rider assigned"); reload(); }
    catch (e) { err(e, "Could not assign rider"); }
  };
  if (["delivered", "rejected", "cancelled", "awaiting_payment"].includes(order.status)) return null;
  return (
    <select value={order.rider_id || ""} onChange={(e) => e.target.value && assign(e.target.value)} data-testid={`admin-assign-rider-${order.order_id}`}
      className="bg-white/5 border border-cyan-500/20 rounded-md text-xs text-slate-200 px-2 py-1">
      <option value="" className="bg-slate-900">Assign rider…</option>
      {riders.filter((r) => !r.is_blocked).map((r) => <option key={r.user_id} value={r.user_id} className="bg-slate-900">{r.name}</option>)}
    </select>
  );
};
